import { lazy, useEffect, useRef, useState, type ReactNode } from "react";
import { WorkspaceRoute } from "./WorkspaceRoute";
import { isPortalPath, usePortalNavigation } from "./usePortalNavigation";
import { accountHref, developerHref, marketingHref } from "./publicHosts";
const ContactWidgetPage = lazy(() => import("./ContactWidget").then(module => ({ default: module.ContactWidget })));
const ContactWidgetSettingsPage = lazy(() => import("./ContactWidgetSettings").then(module => ({ default: module.ContactWidgetSettingsPage })));
import { PlatformBrandIcon } from "./PlatformBrandIcon";
import { BrandLogo } from "./BrandLogo";
import "./portalNavigation.css";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  ArrowRight,
  BadgeCheck,
  Bell,
  Building2,
  Camera,
  ChevronRight,
  CheckCircle2,
  Code2,
  Command,
  EyeOff,
  FileText,
  Gauge,
  Globe2,
  LayoutDashboard,
  LockKeyhole,
  LogIn,
  Menu,
  X,
  MessageSquareText,
  Mic,
  PhoneCall,
  PhoneOff,
  Radio,
  Search,
  ServerCog,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Timer,
  UserCheck,
  UsersRound,
  UserX,
  Video,
  Volume2,
  Webhook,
} from "lucide-react";
import {
  commercialPlans,
  dashboardPreviews,
  developerEntries,
  developerUseCases,
  featureHighlights,
  helpTopics,
  homeDestinations,
  homeStories,
  platformSteps,
  popularHelpArticles,
  previewModes,
  privacyPageSections,
  superadminModules,
  termsPageSections,
  type RosterPreview,
  surfaces,
  trustItems,
  type CommercialPlan,
  type DashboardPreview,
  type DashboardPreviewId,
  type FeatureHighlight,
  type HelpArticle,
  type HelpTopic,
  type PlatformStep,
  type PreviewMode,
  type PreviewModeId,
  type Surface,
  type HomeDestination,
  type HomeStory,
  type LegalPageSection,
  type SuperadminModule,
  type TrustItem,
} from "./content";
import heroProductImage from "./assets/mindset-public-hero-v4.webp";
import avatarMaya from "./assets/avatar-maya.png";
import avatarAndre from "./assets/avatar-andre.png";
import avatarPriya from "./assets/avatar-priya.png";
import avatarGarland from "./assets/avatar-garland.png";
import phoneMockupImg from "./assets/mindset-app-phone-mockup.png";
import videoCallImg from "./assets/mindset-video-call-lifestyle.png";
import groupChatImg from "./assets/mindset-group-chat-lifestyle.png";
import { AccountAccessPage, VerifiedAccessCallbackPage } from "./AccountAccess";
const AccountDashboardPage = lazy(() => import("./ProtectedAccountDashboards").then((module) => ({ default: module.AccountDashboardPage })));
const AccountSuperadminPage = lazy(() => import("./ProtectedAccountDashboards").then((module) => ({ default: module.AccountSuperadminPage })));
const TenantAdminPage = lazy(() => import("./ProtectedAccountDashboards").then((module) => ({ default: module.TenantAdminPage })));
const AccountSecurityPage = lazy(() => import("./AccountSecurity").then(module => ({ default: module.AccountSecurity })));
const DeveloperDocumentationPage = lazy(() => import("./DeveloperDocumentation").then((module) => ({ default: module.DeveloperDocumentationPage })));
const TeamConsolePage = lazy(() => import("./TeamConsole").then((module) => ({ default: module.TeamConsolePage })));
const CommunicationsWorkspace = lazy(() => import("./CommunicationsWorkspace").then((module) => ({ default: module.CommunicationsWorkspace })));

const rosterAvatars: Record<string, string> = {
  maya: avatarMaya,
  andre: avatarAndre,
  family: avatarPriya,
};

const previewIcons: Record<PreviewModeId, LucideIcon> = {
  message: MessageSquareText,
  voice: PhoneCall,
  video: Video,
  groups: UsersRound,
};

const surfaceIcons: Record<string, LucideIcon> = {
  ios: Smartphone,
  android: Radio,
  web: Globe2,
};

const platformIcons: Record<string, LucideIcon> = {
  tenant: LockKeyhole,
  sdk: Code2,
  webhooks: Webhook,
  release: CheckCircle2,
};

const trustIcons: Record<string, LucideIcon> = {
  terms: ShieldCheck,
  privacy: Bell,
  cost: CheckCircle2,
  support: UsersRound,
};

const helpTopicIcons: Record<string, LucideIcon> = {
  "getting-started": BadgeCheck,
  messages: MessageSquareText,
  calls: PhoneCall,
  privacy: ShieldCheck,
  account: LockKeyhole,
  business: Building2,
};

const storyIcons: Record<string, LucideIcon> = {
  messages: MessageSquareText,
  calls: PhoneCall,
  video: Video,
  groups: UsersRound,
  privacy: ShieldCheck,
};

const dashboardIcons: Record<DashboardPreviewId, LucideIcon> = {
  user: MessageSquareText,
  tenant: Building2,
  superadmin: Command,
};

const superadminIcons: Record<string, LucideIcon> = {
  tenants: Building2,
  identity: BadgeCheck,
  evidence: Gauge,
  releases: ServerCog,
  support: UsersRound,
  safety: ShieldCheck,
};

const heroAssuranceItems: Array<{
  id: string;
  icon: LucideIcon;
  label: string;
  value: string;
}> = [
  {
    id: "messages",
    icon: MessageSquareText,
    label: "Messages",
    value: "Encrypted & private",
  },
  {
    id: "voice",
    icon: PhoneCall,
    label: "Voice",
    value: "Free calls",
  },
  {
    id: "video",
    icon: Video,
    label: "Video",
    value: "One tap away",
  },
];

type PublicRoute =
  | "home"
  | "access"
  | "auth-callback"
  | "features"
  | "apps"
  | "platform"
  | "developers"
  | "support"
  | "privacy"
  | "terms"
  | "account-deletion"
  | "dashboard"
  | "tenant-admin"
  | "team"
  | "messages"
  | "superadmin"
  | "docs"
  | "security"
  | "contact-widget"
  | "contact-widget-settings";

export function App() {
  const location = usePortalNavigation();
  const route = currentRoute();
  const [messagesVisited, setMessagesVisited] = useState(route === "messages");
  useEffect(() => { if (route === "messages") setMessagesVisited(true); }, [route]);
  const portal = isPortalPath(new URL(location).pathname);
  return <>
    {portal && (messagesVisited || route === "messages") && <div hidden={route !== "messages"}>
      <WorkspaceRoute name="messages"><CommunicationsWorkspace /></WorkspaceRoute>
    </div>}
    {route !== "messages" && <RoutedPage />}
    {portal && messagesVisited && route !== "messages" && <a className="portal-return-messages" href="/messages">Return to messages and calls</a>}
  </>;
}

function RoutedPage() {
  const route = currentRoute();

  useEffect(() => {
    if (navigator.userAgent.includes("jsdom")) {
      return;
    }

    window.scrollTo({ left: 0, top: 0 });
  }, [route]);

  if (route === "contact-widget") return <WorkspaceRoute name="contact widget"><ContactWidgetPage /></WorkspaceRoute>;
  if (route === "contact-widget-settings") return <WorkspaceRoute name="contact widget settings"><ContactWidgetSettingsPage /></WorkspaceRoute>;
  if (route === "access") {
    return <AccessPage />;
  }

  if (route === "auth-callback") {
    return <VerifiedAccessCallbackPage header={<SiteHeader />} />;
  }

  if (route === "dashboard") {
    return <DashboardRoutePage />;
  }

  if (route === "tenant-admin") {
    return <TenantAdminRoutePage />;
  }
  if (route === "docs") return <WorkspaceRoute name="documentation"><DeveloperDocumentationPage /></WorkspaceRoute>;
  if (route === "security") return <WorkspaceRoute name="security"><AccountSecurityPage /></WorkspaceRoute>;

  if (route === "team") {
    return <TeamConsoleRoutePage />;
  }
  if (route === "messages") return <WorkspaceRoute name="messages"><CommunicationsWorkspace /></WorkspaceRoute>;

  if (route === "superadmin") {
    return <SuperadminRoutePage />;
  }

  if (route === "features") {
    return <FeaturesPage />;
  }

  if (route === "apps") {
    return <AppsPage />;
  }

  if (route === "platform") {
    return <PlatformPage />;
  }

  if (route === "developers") {
    return <DevelopersPage />;
  }

  if (route === "support") {
    return <SupportPage />;
  }

  if (route === "privacy") {
    return <PrivacyPage />;
  }

  if (route === "terms") {
    return <TermsPage />;
  }

  if (route === "account-deletion") {
    return <AccountDeletionPage />;
  }

  return <HomePage />;
}

function HomePage() {
  const [activeModeId, setActiveModeId] = useState<PreviewModeId>("message");
  return (
    <div className="site-shell home-page-shell">
      <SiteHeader />

      <main id="top">
        <section className="hero home-hero" aria-labelledby="hero-title">
          <img className="public-hero-image" src={heroProductImage} alt="Mindset messaging and group video product illustration" fetchPriority="high" />
          <div className="hero-content">
            <p className="eyebrow hero-identity-pill"><ShieldCheck aria-hidden="true" size={15} />People. Not phone numbers.</p>
            <h1 id="hero-title">Mindset</h1>
            <p className="hero-copy">
              Messages, voice, and video with the people who matter.
              One @handle. No phone number required.
            </p>
            <div className="hero-actions">
              <a className="primary-action" href={accountHref("/access?mode=get-started")}>
                <UserCheck aria-hidden="true" size={18} />
                <span>Get Started</span>
                <ArrowRight aria-hidden="true" size={18} />
              </a>
              <a className="secondary-action" href={marketingHref("/features")}>
                <MessageSquareText aria-hidden="true" size={18} />
                <span>See features</span>
                <ArrowRight aria-hidden="true" size={18} />
              </a>
            </div>
            <p className="hero-footnote">Free for individuals. Built for teams, too.</p>
          </div>
        </section>

        <section className="signal-strip" aria-label="Product signals">
          <Signal label="Stay connected" value="Messages, voice & video" />
          <Signal label="Phone numbers" value="None needed" />
          <Signal label="Your identity" value="One verified profile" />
        </section>

        <HomeStoriesSection />
        <ProductPreviewSection
          activeMode={previewModes[activeModeId]}
          activeModeId={activeModeId}
          onModeChange={setActiveModeId}
        />
        <HomeCompanyBand />
        <ClosingCta
          title="Ready to leave your number behind?"
          label="Get Started"
          href="/access?mode=get-started"
        />
      </main>
      <SiteFooter />
    </div>
  );
}

function FeaturesPage() {
  const [activeModeId, setActiveModeId] = useState<PreviewModeId>("message");
  const activeMode = previewModes[activeModeId];

  return (
    <PublicPageShell>
      <section className="hero features-hero" aria-labelledby="features-hero-title">
        <div className="hero-content features-hero-content">
          <p className="eyebrow">Mindset features</p>
          <h1 id="features-hero-title">Messaging, voice & video</h1>
          <p className="hero-copy">
            A conversation can be a message, a call, or a room full of people.
            Stay connected with your @handle, not your phone number.
          </p>
          <div className="hero-actions">
            <a className="primary-action" href={accountHref("/access?mode=get-started")}>
              <span>Get Started Free</span>
              <ArrowRight aria-hidden="true" size={18} />
            </a>
            <a className="secondary-action" href="#interactive-demo">
              <span>Try Interactive Demo</span>
              <ArrowRight aria-hidden="true" size={18} />
            </a>
          </div>
        </div>
      </section>

      <section className="section showcase-split-section">
        <div className="showcase-split">
          <div className="showcase-copy">
            <p className="eyebrow">encrypted text & media</p>
            <h2>Private messaging that feels effortless</h2>
            <p>
              Send high-res photos, voice notes, attachments, and instant emoji
              reactions with complete peace of mind. Your messages stay between you and your recipient.
            </p>
            <ul className="showcase-feature-list">
              <li>
                <ShieldCheck size={18} />
                <span>Zero phone numbers required — connect via <strong>@handle</strong></span>
              </li>
              <li>
                <LockKeyhole size={18} />
                <span>Default end-to-end encryption on all chats</span>
              </li>
              <li>
                <Bell size={18} />
                <span>Private notification previews — hide message content on lock screen</span>
              </li>
            </ul>
          </div>
          <div className="showcase-visual">
            <img
              src={phoneMockupImg}
              alt="Encrypted chat interface mockup"
            />
          </div>
        </div>
      </section>

      <section className="section showcase-split-section showcase-split-section--reverse">
        <div className="showcase-split">
          <div className="showcase-visual">
            <img
              src={videoCallImg}
              alt="HD Voice and Video Call lifestyle preview"
            />
          </div>
          <div className="showcase-copy">
            <p className="eyebrow">voice & video calls</p>
            <h2>Rings directly on your lock screen</h2>
            <p>
              Experience crystal-clear voice and video calling that feels just like a native phone call. Rings directly on your lock screen with instant 1-tap video upgrade.
            </p>
            <ul className="showcase-feature-list">
              <li>
                <PhoneCall size={18} />
                <span>Free HD voice calls between any Mindset handle</span>
              </li>
              <li>
                <Video size={18} />
                <span>Consent-first camera activation — switch to video with 1 tap</span>
              </li>
              <li>
                <LockKeyhole size={18} />
                <span>Encrypted call streams — zero eavesdropping</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section showcase-split-section">
        <div className="showcase-split">
          <div className="showcase-copy">
            <p className="eyebrow">group chats & rooms</p>
            <h2>Group rooms without exposing your cell number</h2>
            <p>
              Hang out in group rooms with friends, family, or teams without leaking your personal phone number to strangers or group members.
            </p>
            <ul className="showcase-feature-list">
              <li>
                <UsersRound size={18} />
                <span>Group rooms for up to 1,000 members with live typing indicators</span>
              </li>
              <li>
                <ShieldCheck size={18} />
                <span>Complete member privacy — cell numbers are never visible</span>
              </li>
              <li>
                <SlidersHorizontal size={18} />
                <span>One-tap muting, admin controls, and instant account wipe</span>
              </li>
            </ul>
          </div>
          <div className="showcase-visual">
            <img
              src={groupChatImg}
              alt="Friends using Mindset group chat"
            />
          </div>
        </div>
      </section>

      <div id="interactive-demo">
        <ProductPreviewSection
          activeMode={activeMode}
          activeModeId={activeModeId}
          onModeChange={setActiveModeId}
        />
      </div>

      <FeatureHighlightsSection />

      <ClosingCta
        title="Ready for private messaging without phone numbers?"
        label="Get Started Free"
        href="/access?mode=get-started"
      />
    </PublicPageShell>
  );
}

function AppsPage() {
  return (
    <PublicPageShell>
      <PageHero
        eyebrow="apps"
        title="Mindset on every device."
        body="iPhone, Android, and the web. One account, the same conversations everywhere."
      />
      <AppsSection />
      <AppsContinuitySection />
      <ClosingCta
        title="Pick a device. Bring your profile."
        label="Create your account"
        href="/access?mode=get-started"
      />
    </PublicPageShell>
  );
}

function PlatformPage() {
  return (
    <PublicPageShell>
      <PageHero
        eyebrow="platform & architecture"
        title="The Mindset platform"
        body="Messaging and calling for your product. One shared platform, with your company's access and controls."
      />
      <section className="section platform-pillars-section">
        <div className="platform-pillar-grid">
          <article className="platform-pillar-card">
            <div className="pillar-icon">
              <Code2 size={24} />
            </div>
            <h3>Unified API Engine</h3>
            <p>
              Connect messaging, voice, and video through shared APIs and SDKs.
            </p>
          </article>
          <article className="platform-pillar-card">
            <div className="pillar-icon">
              <ShieldCheck size={24} />
            </div>
            <h3>Tenant Security Boundary</h3>
            <p>
              Company permissions sit within your subscription's limits, with additional controls for your users.
            </p>
          </article>
          <article className="platform-pillar-card">
            <div className="pillar-icon">
              <Webhook size={24} />
            </div>
            <h3>Real-Time Webhook Stream</h3>
            <p>
              Stream audit events, delivery status, and call lifecycles to your infrastructure with signature verification.
            </p>
          </article>
        </div>
      </section>

      <ClosingCta
        title="Ready to build on Mindset platform?"
        label="View Developer Documentation"
        href="/developers"
      />
    </PublicPageShell>
  );
}

function DevelopersPage() {
  return (
    <PublicPageShell>
      <PageHero
        eyebrow="developers"
        title="Build on Mindset."
        body="API docs, SDKs, and a clear path from first request to launch."
      />
      <DeveloperSection />
      <DeveloperUseCasesSection />
    </PublicPageShell>
  );
}

function SupportPage() {
  const [query, setQuery] = useState("");
  const matches = (item: { title: string; body: string }) => `${item.title} ${item.body}`.toLowerCase().includes(query.trim().toLowerCase());
  const topics = helpTopics.filter(matches);
  const articles = popularHelpArticles.filter(matches);
  return (
    <PublicPageShell>
      <SupportHero query={query} onQueryChange={setQuery} />
      {topics.length + articles.length === 0 ? <p className="support-no-results" role="status">No results for &quot;{query}&quot;.</p> : null}
      <HelpTopicsSection topics={topics} />
      <PopularHelpSection articles={articles} />
      <ContactSupportSection />
    </PublicPageShell>
  );
}

function PrivacyPage() {
  return (
    <PublicPageShell>
      <LegalPage
        eyebrow="privacy"
        title="Privacy Policy"
        body="How Mindset protects your personal communication, identity, and messages — without phone numbers or tracking."
        sections={privacyPageSections}
      />
    </PublicPageShell>
  );
}

function TermsPage() {
  return (
    <PublicPageShell>
      <LegalPage
        eyebrow="terms"
        title="Terms of Use"
        body="The rules for using Mindset — including free individual calls and acceptable use."
        sections={termsPageSections}
      />
    </PublicPageShell>
  );
}

function AccountDeletionPage() {
  return (
    <PublicPageShell>
      <LegalPage
        eyebrow="account deletion"
        title="Delete your Mindset account"
        body="You can delete your Mindset account at any time. Here is how it works."
        sections={[
          {
            title: "How to delete your account",
            body:
              "Open Settings in the app and choose Delete account, or contact support if you cannot sign in.",
            points: [
              "We confirm it is really you before anything is deleted",
              "Your devices are signed out and your sessions are removed",
              "Your profile is removed from Mindset",
            ],
          },
          {
            title: "What we may keep",
            body:
              "Some records may need to remain for legal, security, or fraud-prevention reasons.",
            points: [
              "We keep only what the law or safety requires",
              "Support can explain what was kept and why",
            ],
          },
        ]}
      />
    </PublicPageShell>
  );
}

function PublicPageShell({ children }: { children: ReactNode }) {
  return (
    <div className="site-shell public-page-shell">
      <SiteHeader />
      <main>{children}</main>
      <SiteFooter />
    </div>
  );
}

function PageHero({
  eyebrow,
  title,
  body,
}: {
  eyebrow: string;
  title: string;
  body: string;
}) {
  return (
    <section className="page-hero" aria-labelledby="page-title">
      <p className="eyebrow">{eyebrow}</p>
      <h1 id="page-title">{title}</h1>
      <p>{body}</p>
    </section>
  );
}

function HomeDestinationSection() {
  return (
    <section
      className="section home-destination-section"
      aria-labelledby="home-destinations-title"
    >
      <div className="section-heading">
        <p className="eyebrow">explore</p>
        <h2 id="home-destinations-title">Where to next?</h2>
        <p>Take a closer look at whatever matters most to you.</p>
      </div>
      <div className="home-destination-grid">
        {homeDestinations.map((destination) => (
          <HomeDestinationCard
            destination={destination}
            key={destination.id}
          />
        ))}
      </div>
    </section>
  );
}

function HomeDestinationCard({
  destination,
}: {
  destination: HomeDestination;
}) {
  return (
    <article className="home-destination-card">
      <h3>{destination.title}</h3>
      <p>{destination.body}</p>
      <a href={accountHref(marketingHref(destination.href))}>
        <span>{destination.label}</span>
        <ArrowRight aria-hidden="true" size={16} />
      </a>
    </article>
  );
}

function AppsSection() {
  return (
    <section className="section apps-section" id="apps" aria-labelledby="apps-title">
      <div className="section-heading">
        <p className="eyebrow">your devices</p>
        <h2 id="apps-title">Built for the device in your hand</h2>
        <p>
          Native apps for iPhone and Android, plus Mindset on the web.
        </p>
      </div>
      <div className="surface-grid">
        {surfaces.map((surface) => (
          <SurfaceCard key={surface.id} surface={surface} />
        ))}
      </div>
    </section>
  );
}

function PlatformSection() {
  return (
    <section className="section platform-section" id="platform" aria-labelledby="platform-title">
      <div className="section-heading">
        <p className="eyebrow">what you get</p>
        <h2 id="platform-title">Ready-made building blocks</h2>
        <p>
          The same messaging, calling, and identity that powers Mindset —
          available to your product.
        </p>
      </div>
      <div className="platform-rail">
        {platformSteps.map((step) => (
          <PlatformStepCard key={step.id} step={step} />
        ))}
      </div>
    </section>
  );
}

function CommercialSection() {
  return (
    <section className="section commercial-section" id="pricing" aria-labelledby="pricing-title">
      <div className="section-heading">
        <p className="eyebrow">who it's for</p>
        <h2 id="pricing-title">Free for people. Built for companies.</h2>
        <p>
          The Mindset app is free. Companies and enterprises build on the same
          platform.
        </p>
      </div>
      <div className="commercial-grid">
        {commercialPlans.map((plan) => (
          <CommercialCard key={plan.id} plan={plan} />
        ))}
      </div>
    </section>
  );
}

function DeveloperSection() {
  return (
    <section className="section developer-section" id="developers" aria-labelledby="developers-title">
      <div className="section-heading">
        <p className="eyebrow">start here</p>
        <h2 id="developers-title">Everything you need to start building</h2>
        <p>Docs, SDKs, and onboarding — one click each.</p>
      </div>
      <div className="developer-grid">
        {developerEntries.map((entry) => (
          <article className="developer-card" key={entry.id}>
            <span className="card-icon" aria-hidden="true">
              <Code2 size={22} />
            </span>
            <h3>{entry.title}</h3>
            <p>{entry.body}</p>
            <a href={developerHref(entry.href)}>
              <span>{entry.label}</span>
              <ArrowRight aria-hidden="true" size={16} />
            </a>
          </article>
        ))}
      </div>
    </section>
  );
}

function SupportHero({ query, onQueryChange }: { query: string; onQueryChange: (value: string) => void }) {
  return (
    <section className="support-hero" aria-labelledby="support-title">
      <div>
        <p className="eyebrow">Mindset Help Center</p>
        <h1 id="support-title">How can we help?</h1>
        <p>
          Answers about your account, messages, free calls, privacy, and
          deleting your account.
        </p>
      </div>
      <label className="support-search">
        <Search aria-hidden="true" size={22} />
        <span>Search help articles</span>
        <input
          aria-label="Search help articles"
          placeholder="Search help articles"
          type="search"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
        />
      </label>
    </section>
  );
}

function HelpTopicsSection({ topics = helpTopics }: { topics?: readonly HelpTopic[] }) {
  return (
    <section
      className="section help-topics-section"
      aria-labelledby="help-topics-title"
    >
      <div className="section-heading">
        <p className="eyebrow">browse help topics</p>
        <h2 id="help-topics-title">What do you need help with?</h2>
      </div>
      <div className="help-topic-list">
        {topics.map((topic) => (
          <HelpTopicRow key={topic.id} topic={topic} />
        ))}
      </div>
    </section>
  );
}

function HelpTopicRow({ topic }: { topic: HelpTopic }) {
  const Icon = helpTopicIcons[topic.id] ?? FileText;

  return (
    <a className="help-topic-row" href={accountHref(marketingHref(topic.href))}>
      <span className="help-topic-icon" aria-hidden="true">
        <Icon size={23} />
      </span>
      <span>
        <strong>{topic.title}</strong>
        <em>{topic.body}</em>
      </span>
      <ChevronRight aria-hidden="true" size={20} />
    </a>
  );
}

function PopularHelpSection({ articles = popularHelpArticles }: { articles?: readonly HelpArticle[] }) {
  return (
    <section
      className="section popular-help-section"
      aria-labelledby="popular-help-title"
    >
      <div className="section-heading">
        <p className="eyebrow">popular articles</p>
        <h2 id="popular-help-title">Common questions</h2>
      </div>
      <div className="popular-help-list">
        {articles.map((article) => (
          <PopularHelpArticle key={article.id} article={article} />
        ))}
      </div>
    </section>
  );
}

function PopularHelpArticle({ article }: { article: HelpArticle }) {
  return (
    <a className="popular-help-article" href={accountHref(marketingHref(article.href))}>
      <span>
        <strong>{article.title}</strong>
        <em>{article.body}</em>
      </span>
      <ChevronRight aria-hidden="true" size={20} />
    </a>
  );
}

function ContactSupportSection() {
  return (
    <section
      className="section contact-support-section"
      aria-labelledby="contact-support-title"
    >
      <div className="contact-support-panel">
        <div>
          <p className="eyebrow">still need help?</p>
          <h2 id="contact-support-title">Get the right kind of help</h2>
          <p>
            Account support and resources for teams building on Mindset.
          </p>
        </div>
        <div className="contact-support-actions">
          <a href={accountHref("/access")}>
            <BadgeCheck aria-hidden="true" size={18} />
            <span>Account help</span>
          </a>
          <a href={marketingHref("/developers")}>
            <Code2 aria-hidden="true" size={18} />
            <span>Company and developer help</span>
          </a>
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  return (
    <section className="section trust-section" id="trust" aria-labelledby="trust-title">
      <div className="section-heading">
        <p className="eyebrow">trust</p>
        <h2 id="trust-title">Built to be trusted with your product</h2>
        <p>
          Companies need clear promises before they build on Mindset. Here are
          ours.
        </p>
      </div>
      <div className="trust-grid">
        {trustItems.map((item) => (
          <TrustCard key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}

const legalIcons: Record<string, LucideIcon> = {
  "1. Verified identity without phone numbers": UserCheck,
  "2. End-to-end encryption & call security": LockKeyhole,
  "3. Data minimization & zero ad tracking": EyeOff,
  "4. Personal & enterprise workspace isolation": Building2,
  "5. Your rights & account deletion": UserX,
  "Using Mindset": ShieldCheck,
  "Free individual calls": PhoneCall,
  "No emergency calling": PhoneOff,
  "Companies building on Mindset": Building2,
};

function LegalPage({
  eyebrow,
  title,
  body,
  note,
  sections,
}: {
  eyebrow: string;
  title: string;
  body: string;
  note?: string;
  sections: readonly LegalPageSection[];
}) {
  return (
    <>
      <PageHero eyebrow={eyebrow} title={title} body={body} />
      <section className="section legal-section" aria-label={title}>
        {note ? <p className="legal-draft-note">{note}</p> : null}
        <div className="legal-grid">
          {sections.map((section, index) => {
            const Icon = legalIcons[section.title] || ShieldCheck;
            const isCenteredBottom = index === 4;
            return (
              <article
                className={`legal-card ${isCenteredBottom ? "legal-card--centered-bottom" : ""}`}
                key={section.title}
              >
                <div className="legal-card-header">
                  <div className="legal-card-icon" aria-hidden="true">
                    <Icon size={22} />
                  </div>
                  <h2>{section.title}</h2>
                </div>
                <p>{section.body}</p>
                <ul>
                  {section.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
}

function HeroAssuranceStrip() {
  return (
    <div className="hero-assurance-strip" aria-label="Mindset core capabilities">
      {heroAssuranceItems.map((item) => {
        const Icon = item.icon;
        return (
          <span key={item.id}>
            <Icon aria-hidden="true" size={16} />
            <strong>{item.label}</strong>
            <em>{item.value}</em>
          </span>
        );
      })}
    </div>
  );
}

function HeroBackdrop() {
  return (
    <div className="hero-backdrop">
      <img
        alt="Mindset app preview showing secure messaging and video calling"
        className="hero-image"
        src={heroProductImage}
      />
    </div>
  );
}

function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const route = currentRoute();
  const access = route === "access" || route === "auth-callback";
  useEffect(() => { setMenuOpen(false); }, [route]);
  return (
    <header className={`topbar${access ? " topbar--access" : ""}`} onKeyDown={(event) => {
      if (event.key === "Escape" && menuOpen) {
        setMenuOpen(false);
        menuButton.current?.focus();
      }
    }}>
      <a className="brand-lockup" href={marketingHref("/")} aria-label="Mindset home">
        <BrandLogo />
      </a>
      <nav className={`topnav${menuOpen ? " is-open" : ""}`} id="public-navigation" aria-label="Primary navigation">
        {(["features", "apps", "platform", "developers", "support"] as const).map((path) => <a key={path} href={marketingHref(`/${path}`)} aria-current={route === path ? "page" : undefined}>{path[0].toUpperCase() + path.slice(1)}</a>)}
      </nav>
      <div className="header-actions" aria-label="Account actions">
        <a className="header-action header-action--ghost" href={marketingHref("/apps")}>
          <Smartphone aria-hidden="true" size={16} />
          <span>Download App</span>
        </a>
        {!access && <a className="header-action header-action--solid" href={accountHref("/access")}>
          <LogIn aria-hidden="true" size={16} />
          <span>Sign in</span>
          <ArrowRight aria-hidden="true" size={16} />
        </a>}
        <button ref={menuButton} type="button" className="public-menu-toggle" aria-label={menuOpen ? "Close navigation" : "Open navigation"} aria-expanded={menuOpen} aria-controls="public-navigation" onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X size={20} /> : <Menu size={20} />}</button>
      </div>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="site-footer-inner">
        <div className="site-footer-brand">
          <a className="brand-lockup" href={marketingHref("/")} aria-label="Mindset home">
            <BrandLogo />
          </a>
          <p>
            Private messages, free calls, and video — with one verified profile
            instead of a phone number.
          </p>
        </div>
        <div className="site-footer-groups">
          <nav className="site-footer-group" aria-label="Product">
            <strong>Product</strong>
            <a href={marketingHref("/features")}>Features</a>
            <a href={marketingHref("/apps")}>Apps</a>
            <a href={marketingHref("/platform")}>Platform</a>
            <a href={marketingHref("/developers")}>Developers</a>
          </nav>
          <nav className="site-footer-group" aria-label="Help">
            <strong>Help</strong>
            <a href={marketingHref("/support")}>Support</a>
            <a href={marketingHref("/privacy")}>Privacy</a>
            <a href={marketingHref("/terms")}>Terms of Use</a>
            <a href={marketingHref("/account-deletion")}>Account deletion</a>
          </nav>
        </div>
      </div>
      <div className="site-footer-bottom">
        <p>© 2026 Mindset. All rights reserved.</p>
      </div>
    </footer>
  );
}

function AccessPage() {
  return <AccountAccessPage header={<SiteHeader />} />;
}

function DashboardRoutePage() {
  return <WorkspaceRoute name="account"><AccountDashboardPage /></WorkspaceRoute>;
}

function TenantAdminRoutePage() {
  return <WorkspaceRoute name="administration"><TenantAdminPage /></WorkspaceRoute>;
}

function TeamConsoleRoutePage() {
  return <WorkspaceRoute name="team"><TeamConsolePage /></WorkspaceRoute>;
}

function SuperadminRoutePage() {
  return <WorkspaceRoute name="platform operations"><AccountSuperadminPage /></WorkspaceRoute>;
}

function currentRoute(): PublicRoute {
  if (typeof window === "undefined") {
    return "home";
  }

  const pathname = window.location.pathname.replace(/\/+$/u, "") || "/";
  if (pathname === "/contact-widget") return "contact-widget";
  if (pathname === "/docs") return "docs";
  if (pathname === "/security") return "security";
  if (pathname === "/contact-widget-settings") return "contact-widget-settings";

  if (pathname === "/access") {
    return "access";
  }

  if (pathname === "/auth/callback") {
    return "auth-callback";
  }

  if (pathname === "/dashboard") {
    return "dashboard";
  }

  if (pathname === "/tenant-admin") {
    return "tenant-admin";
  }

  if (pathname === "/team") {
    return "team";
  }
  if (pathname === "/messages") return "messages";

  if (pathname === "/superadmin") {
    return "superadmin";
  }

  if (pathname === "/features") {
    return "features";
  }

  if (pathname === "/apps") {
    return "apps";
  }

  if (pathname === "/platform") {
    return "platform";
  }

  if (pathname === "/developers") {
    return "developers";
  }

  if (pathname === "/support") {
    return "support";
  }

  if (pathname === "/privacy") {
    return "privacy";
  }

  if (pathname === "/terms") {
    return "terms";
  }

  if (pathname === "/account-deletion") {
    return "account-deletion";
  }

  return "home";
}

function accessModeFromLocation(): "signin" | "get-started" {
  if (typeof window === "undefined") {
    return "signin";
  }

  const requestedMode = new URLSearchParams(window.location.search).get("mode");

  return requestedMode === "get-started" ? "get-started" : "signin";
}

function HomeStoriesSection() {
  return (
    <section
      className="section home-stories-section"
      id="features"
      aria-labelledby="features-title"
    >
      <div className="section-heading">
        <p className="eyebrow">why mindset</p>
        <h2 id="features-title">Everything you need to stay close</h2>
        <p>
          Message, call, and video chat — all from one verified profile that
          keeps your number private.
        </p>
      </div>
      <div className="home-story-grid">
        {homeStories.map((story) => (
          <HomeStoryCard key={story.id} story={story} />
        ))}
      </div>
    </section>
  );
}

function HomeStoryCard({ story }: { story: HomeStory }) {
  const Icon = storyIcons[story.id] ?? MessageSquareText;
  return (
    <article className="home-story-card">
      <div className="home-story-icon">
        <Icon aria-hidden="true" size={22} />
      </div>
      <span>{story.eyebrow}</span>
      <h3>{story.title}</h3>
      <p>{story.body}</p>
      {story.points && story.points.length > 0 && (
        <ul>
          {story.points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      )}
    </article>
  );
}

function HomeCompanyBand() {
  return (
    <section className="section quiet-band-section" aria-label="For companies">
      <div className="quiet-band">
        <p>
          <strong>Building a product?</strong> Put Mindset inside it.
        </p>
        <a href={marketingHref("/platform")}>
          <span>Visit the platform</span>
          <ArrowRight aria-hidden="true" size={16} />
        </a>
      </div>
    </section>
  );
}

function ClosingCta({
  title,
  label,
  href,
}: {
  title: string;
  label: string;
  href: string;
}) {
  return (
    <section className="section closing-cta" aria-label="Get started">
      <div className="closing-cta-panel">
        <h2>{title}</h2>
        <a className="cta-button" href={accountHref(marketingHref(href))}>
          <span>{label}</span>
          <ArrowRight aria-hidden="true" size={17} />
        </a>
      </div>
    </section>
  );
}

const featureIcons: Record<string, LucideIcon> = {
  messages: MessageSquareText,
  calls: PhoneCall,
  video: Video,
  groups: UsersRound,
  privacy: ShieldCheck,
};

function FeatureHighlightsSection() {
  return (
    <section
      className="section feature-highlights-section"
      aria-labelledby="feature-highlights-title"
    >
      <div className="section-heading">
        <p className="eyebrow">the essentials</p>
        <h2 id="feature-highlights-title">Core capabilities</h2>
        <p>Everything built around your @handle and total privacy.</p>
      </div>
      <div className="feature-list">
        {featureHighlights.map((feature) => (
          <FeatureBlock feature={feature} key={feature.id} />
        ))}
      </div>
    </section>
  );
}

function FeatureBlock({ feature }: { feature: FeatureHighlight }) {
  const Icon = featureIcons[feature.id] ?? MessageSquareText;
  return (
    <article className="feature-block">
      <div className="feature-block-icon">
        <Icon aria-hidden="true" size={22} />
      </div>
      <div className="feature-block-copy">
        <h3>{feature.title}</h3>
        <p>{feature.body}</p>
        {feature.points && feature.points.length > 0 && (
          <ul>
            {feature.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

function AppsContinuitySection() {
  return (
    <section className="section quiet-band-section" aria-label="One account everywhere">
      <div className="quiet-band quiet-band--stacked">
        <p>
          <strong>One account, everywhere.</strong> Sign in with your verified
          profile on any device. Start a chat on your phone, continue on your
          laptop.
        </p>
        <p className="quiet-band-note">
          Individual calls between Mindset users are free. Calls use your
          internet connection, so carrier data charges may apply.
        </p>
      </div>
    </section>
  );
}

function DeveloperUseCasesSection() {
  return (
    <section
      className="section developer-usecases-section"
      aria-labelledby="developer-usecases-title"
    >
      <div className="section-heading">
        <p className="eyebrow">what you can build</p>
        <h2 id="developer-usecases-title">A few places to start</h2>
      </div>
      <div className="usecase-list">
        {developerUseCases.map((useCase) => (
          <article className="usecase-item" key={useCase.id}>
            <h3>{useCase.title}</h3>
            <p>{useCase.body}</p>
          </article>
        ))}
      </div>
      <p className="usecase-help-line">
        Questions before you start? Visit the{" "}
        <a href="/platform">platform page</a> or the{" "}
        <a href="/support">help center</a>.
      </p>
    </section>
  );
}

function ProductPreviewSection({
  activeMode,
  activeModeId,
  onModeChange,
}: {
  activeMode: PreviewMode;
  activeModeId: PreviewModeId;
  onModeChange: (modeId: PreviewModeId) => void;
}) {
  return (
    <section
      className="section product-preview-section"
      id="product-preview"
      aria-labelledby="product-preview-title"
    >
      <div className="section-heading">
        <p className="eyebrow">interactive demo</p>
        <h2 id="product-preview-title">Experience Mindset live</h2>
        <p>
          Switch between text, voice, video, and groups to see how native
          communication feels.
        </p>
      </div>
      <div className="product-preview-layout">
        <div className="mode-switch" aria-label="Preview mode">
          {Object.values(previewModes).map((mode) => {
            const Icon = previewIcons[mode.id];
            return (
              <button
                key={mode.id}
                className={mode.id === activeModeId ? "active" : ""}
                aria-pressed={mode.id === activeModeId}
                type="button"
                onClick={() => onModeChange(mode.id)}
              >
                <Icon aria-hidden="true" size={16} />
                <span>{mode.label}</span>
              </button>
            );
          })}
        </div>
        <ModeStory mode={activeMode} />
      </div>
    </section>
  );
}

function DashboardSection({
  activeDashboard,
  activeDashboardId,
  onDashboardChange,
}: {
  activeDashboard: DashboardPreview;
  activeDashboardId: DashboardPreviewId;
  onDashboardChange: (dashboardId: DashboardPreviewId) => void;
}) {
  const Icon = dashboardIcons[activeDashboard.id];

  return (
    <section
      className="section dashboard-section"
      id="dashboards"
      aria-labelledby="dashboards-title"
    >
      <div className="section-heading">
        <p className="eyebrow">after sign in</p>
        <h2 id="dashboards-title">Dashboards for users, tenants, and Mindset owners</h2>
        <p>
          Mindset sign-in should lead to a clear role-aware home: a simple
          communication dashboard for users, an operations workspace for
          tenants, and protected controls for platform operators.
        </p>
      </div>

      <div className="dashboard-workspace" aria-label="Mindset dashboard preview">
        <div className="dashboard-tabs" aria-label="Dashboard role">
          {dashboardPreviews.map((dashboard) => {
            const TabIcon = dashboardIcons[dashboard.id];
            return (
              <button
                key={dashboard.id}
                className={dashboard.id === activeDashboardId ? "active" : ""}
                type="button"
                onClick={() => onDashboardChange(dashboard.id)}
              >
                <TabIcon aria-hidden="true" size={17} />
                <span>{dashboard.label}</span>
              </button>
            );
          })}
        </div>

          <div className="dashboard-shell">
            <div className="dashboard-sidebar" aria-label="Workspace navigation">
              <strong>Mindset</strong>
            <a href={accountHref("/dashboard")}>
              <LayoutDashboard aria-hidden="true" size={16} />
              <span>Dashboard</span>
            </a>
            <a href="/features">
              <MessageSquareText aria-hidden="true" size={16} />
              <span>Conversations</span>
            </a>
            <a href="/platform">
              <Code2 aria-hidden="true" size={16} />
              <span>Integrations</span>
            </a>
          </div>

          <div className="dashboard-main">
            <div className="dashboard-title-row">
              <div>
                <span>{activeDashboard.eyebrow}</span>
                <h3>
                  <Icon aria-hidden="true" size={25} />
                  {activeDashboard.title}
                </h3>
                <p>{activeDashboard.summary}</p>
              </div>
              <div className="dashboard-status">
                <span>{activeDashboard.statusLabel}</span>
                <strong>{activeDashboard.statusValue}</strong>
              </div>
            </div>

            <div className="dashboard-actions" aria-label="Dashboard actions">
              <button type="button">
                <ArrowRight aria-hidden="true" size={16} />
                <span>{activeDashboard.primaryAction}</span>
              </button>
              <button type="button">
                <Activity aria-hidden="true" size={16} />
                <span>{activeDashboard.secondaryAction}</span>
              </button>
            </div>

            <div className="dashboard-metrics">
              {activeDashboard.metrics.map((metric) => (
                <article className="dashboard-metric" key={metric.label}>
                  <span>{metric.label}</span>
                  <strong>{metric.value}</strong>
                  <p>{metric.detail}</p>
                </article>
              ))}
            </div>

            <div className="dashboard-lanes">
              {activeDashboard.lanes.map((lane) => (
                <article className="dashboard-lane" key={lane.title}>
                  <span>{lane.state}</span>
                  <h4>{lane.title}</h4>
                  <p>{lane.detail}</p>
                </article>
              ))}
            </div>
          </div>

          <aside className="dashboard-activity" aria-label="Recent dashboard activity">
            <strong>Recent activity</strong>
            <ol>
              {activeDashboard.activity.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </aside>
        </div>
      </div>
    </section>
  );
}

function SuperadminSection() {
  return (
    <section
      className="section superadmin-section"
      id="superadmin"
      aria-labelledby="superadmin-title"
    >
      <div className="section-heading">
        <p className="eyebrow">platform operations</p>
        <h2 id="superadmin-title">Protected operations for the business behind Mindset</h2>
        <p>
          The platform operations section gives authorized Mindset staff tenant governance,
          identity routing, release evidence, support oversight, and audit
          visibility without weakening approval boundaries.
        </p>
      </div>
      <div className="superadmin-grid">
        {superadminModules.map((module) => (
          <SuperadminModuleCard key={module.id} module={module} />
        ))}
      </div>
    </section>
  );
}

function SuperadminModuleCard({ module }: { module: SuperadminModule }) {
  const Icon = superadminIcons[module.id] ?? SlidersHorizontal;
  return (
    <article className="superadmin-card">
      <div className="superadmin-card-top">
        <div className="card-icon" aria-hidden="true">
          <Icon size={22} />
        </div>
        <span>{module.state}</span>
      </div>
      <h3>{module.title}</h3>
      <p>{module.body}</p>
      <ul>
        {module.checks.map((check) => (
          <li key={check}>
            <CheckCircle2 aria-hidden="true" size={15} />
            <span>{check}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function ModeStory({ mode }: { mode: PreviewMode }) {
  const Icon = previewIcons[mode.id];
  return (
    <div className="mode-story" aria-label={`${mode.label} product preview`}>
      <div className="mode-story-topline">
        <span className="status-dot" aria-hidden="true" />
        <span>{mode.status}</span>
      </div>
      <div className="mode-story-body">
        {mode.id === "groups" && (
          <div className="avatar-stack" aria-hidden="true">
            <span>
              <img src={avatarMaya} alt="Maya" className="avatar-stack-img" />
            </span>
            <span>
              <img src={avatarAndre} alt="Andre" className="avatar-stack-img" />
            </span>
            <span>
              <img src={avatarPriya} alt="Priya" className="avatar-stack-img" />
            </span>
          </div>
        )}
        <Icon aria-hidden="true" size={24} />
        <h2>{mode.title}</h2>
        <p>{mode.body}</p>
      </div>
      <div className="conversation-preview">
        <ModePreview mode={mode} />
      </div>
      <div className="metric-row">
        {mode.metrics.map((metric) => (
          <span key={metric}>{metric}</span>
        ))}
      </div>
    </div>
  );
}

function ModePreview({ mode }: { mode: PreviewMode }) {
  if (mode.rosterPreview) {
    return (
      <div
        className="conversation-roster-preview"
        aria-label="Conversation roster preview"
      >
        {mode.rosterPreview.map((row) => (
          <RosterPreviewRow key={row.id} row={row} />
        ))}
      </div>
    );
  }

  if (mode.id === "voice") {
    return (
      <div className="call-preview" aria-label="Voice call controls preview">
        <div className="call-identity">
          <span className="call-avatar" aria-hidden="true">
            <img src={avatarMaya} alt="Maya Lane" className="call-avatar-img" />
          </span>
          <div>
            <strong>Maya Lane</strong>
            <small>{mode.primaryLine}</small>
          </div>
        </div>
        <div className="call-timer">
          <Timer aria-hidden="true" size={15} />
          <span>00:42</span>
        </div>
        <div className="call-controls" aria-label="Native call controls">
          <button type="button" aria-label="Mute call preview">
            <Mic aria-hidden="true" size={16} />
          </button>
          <button type="button" aria-label="Speaker call preview">
            <Volume2 aria-hidden="true" size={16} />
          </button>
          <button className="destructive" type="button" aria-label="End call preview">
            <PhoneOff aria-hidden="true" size={16} />
          </button>
        </div>
      </div>
    );
  }

  if (mode.id === "video") {
    return (
      <div className="video-preview" aria-label="Video consent preview">
        <div className="video-grid">
          <div className="video-pane video-pane--active">
            <span className="video-avatar" aria-hidden="true">
              <img src={avatarGarland} alt="Garland" className="video-avatar-img" />
            </span>
            <strong>Camera ready</strong>
          </div>
          <div className="video-pane">
            <span className="video-avatar" aria-hidden="true">
              <img src={avatarMaya} alt="Maya Lane" className="video-avatar-img" />
            </span>
            <strong>Audio first</strong>
          </div>
        </div>
        <div className="video-consent">
          <Camera aria-hidden="true" size={18} />
          <div>
            <strong>{mode.primaryLine}</strong>
            <span>{mode.secondaryLine}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <p>{mode.primaryLine}</p>
      <span>{mode.secondaryLine}</span>
    </>
  );
}

function CommunicationDock() {
  const items: Array<{
    id: string;
    icon: LucideIcon;
    label: string;
    value: string;
    detail: string;
  }> = [
    {
      id: "inbox",
      icon: MessageSquareText,
      label: "Messages",
      value: "3 unread",
      detail: "Maya sent two photos.",
    },
    {
      id: "call",
      icon: PhoneCall,
      label: "Calls",
      value: "00:42",
      detail: "Free call with Maya, still going.",
    },
    {
      id: "video",
      icon: Video,
      label: "Video",
      value: "Ready",
      detail: "Switch whenever you both want.",
    },
    {
      id: "privacy",
      icon: ShieldCheck,
      label: "Privacy",
      value: "On",
      detail: "Previews stay hidden until you unlock.",
    },
  ];

  return (
    <div className="communication-dock" aria-label="Live communication dock">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <article className="communication-dock-item" key={item.id}>
            <Icon aria-hidden="true" size={17} />
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <p>{item.detail}</p>
          </article>
        );
      })}
    </div>
  );
}

function RosterPreviewRow({ row }: { row: RosterPreview }) {
  const avatarUrl = rosterAvatars[row.id];

  return (
    <div className="roster-preview-row">
      <span className="roster-avatar" aria-hidden="true">
        {avatarUrl ? (
          <img src={avatarUrl} alt={row.title} className="roster-avatar-img" />
        ) : (
          initials(row.title)
        )}
      </span>
      <div className="roster-copy">
        <strong>{row.title}</strong>
        <p>
          {row.sender}: {row.body}
        </p>
      </div>
      <div className="roster-meta">
        <time>{row.timestamp}</time>
        {row.unreadCount > 0 ? (
          <span className="unread-count">{row.unreadCount} unread</span>
        ) : (
          <span>{row.state}</span>
        )}
      </div>
    </div>
  );
}

function initials(value: string) {
  return value
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

const signalIcons: Record<string, LucideIcon> = {
  Security: ShieldCheck,
  "Phone numbers": PhoneOff,
  "Your identity": UserCheck,
};

function Signal({ label, value }: { label: string; value: string }) {
  const Icon = signalIcons[label] ?? ShieldCheck;
  return (
    <div className="signal-card">
      <span className="signal-card-icon" aria-hidden="true">
        <Icon size={24} />
      </span>
      <span className="signal-card-text">
        <span className="signal-card-label">{label}</span>
        <strong className="signal-card-value">{value}</strong>
      </span>
    </div>
  );
}

function SurfaceCard({ surface }: { surface: Surface }) {
  const Icon = surfaceIcons[surface.id] ?? Smartphone;
  return (
    <article className="surface-card">
      <span className="card-icon" aria-hidden="true">
        {surface.id === "ios" || surface.id === "android" ? <PlatformBrandIcon platform={surface.id} /> : <Icon size={22} />}
      </span>
      <h3>{surface.title}</h3>
      <p>{surface.body}</p>
      <ul>
        {surface.details.map((detail) => (
          <li key={detail}>{detail}</li>
        ))}
      </ul>
    </article>
  );
}

function PlatformStepCard({ step }: { step: PlatformStep }) {
  const Icon = platformIcons[step.id] ?? CheckCircle2;
  return (
    <article className="platform-card">
      <div className="icon-box">
        <Icon aria-hidden="true" size={22} />
      </div>
      <div>
        <h3>{step.title}</h3>
        <p>{step.body}</p>
        <strong>{step.proof}</strong>
      </div>
    </article>
  );
}

function CommercialCard({ plan }: { plan: CommercialPlan }) {
  return (
    <article className="commercial-card">
      <span>{plan.audience}</span>
      <h3>{plan.title}</h3>
      <b>{plan.price}</b>
      <p>{plan.body}</p>
      <ul>
        {plan.includes.map((item) => (
          <li key={item}>
            <CheckCircle2 aria-hidden="true" size={16} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <strong>{plan.boundary}</strong>
    </article>
  );
}

function TrustCard({ item }: { item: TrustItem }) {
  const Icon = trustIcons[item.id] ?? ShieldCheck;
  return (
    <article className="trust-card">
      <span className="card-icon" aria-hidden="true">
        <Icon size={22} />
      </span>
      <h3>{item.title}</h3>
      <p>{item.body}</p>
    </article>
  );
}
